import { Head, useForm } from "@inertiajs/react";
import { Plus, Target, Trophy, Wand2 } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import FormField from "@/components/forms/FormField";
import MoneyField from "@/components/forms/MoneyField";
import RewardManager from "@/components/schools/rewards/RewardManager";
import { formatRoom, formatGrade } from "@/lib/classroom";
import { formatMoney, formatNumber, percentOf } from "@/lib/format";
import { suggestMinuteGoal, MINUTES_PER_DAY } from "@/lib/campaignPlanning";

/**
 * One classroom's goals and classroom rewards. Reachable by the class's
 * teacher(s) and school admins. Goals are nudges with a progress bar;
 * rewards are what the class earns together when it gets there.
 */
export default function Milestones({ school, membership, campaign, classroom, milestones, create_path, update_goals_path, back_path, back_label }) {
  const room = formatRoom(classroom.name);
  const goals = { money_goal_cents: classroom.money_goal_cents, minute_goal: classroom.minute_goal };

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      eyebrow={`${formatGrade(classroom.grade) || "Classroom"} · ${room}`}
      title="Goals & rewards"
      back={{ href: back_path, label: back_label ? `Back to ${back_label}` : "Back" }}
    >
      <Head title={`${room} · Goals & rewards`} />

      <div className="space-y-8">
        <GoalsCard classroom={classroom} campaign={campaign} updateGoalsPath={update_goals_path} />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Classroom rewards
            </CardTitle>
            <CardDescription>
              When {room}'s fundraising or reading total reaches a reward, everyone in the class earns it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RewardManager
              rewards={milestones}
              scope="classroom"
              createPath={create_path}
              locked={campaign.locked}
              showProgress
              goals={goals}
              addLabel="Add a classroom reward"
              emptyState={({ onAdd }) => (
                <div className="py-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    No classroom rewards yet. Add one your class can earn together, like a movie afternoon at 5,000 minutes.
                  </p>
                  <Button variant="outline" className="mt-4" onClick={onAdd}>
                    <Plus className="h-4 w-4" />
                    Add a classroom reward
                  </Button>
                </div>
              )}
            />
          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
}

function GoalsCard({ classroom, campaign, updateGoalsPath }) {
  // money_goal_dollars is what's typed; the controller converts it to cents.
  const { data, setData, patch, processing, errors } = useForm({
    minute_goal: classroom.minute_goal ?? "",
    money_goal_dollars: classroom.money_goal_cents != null ? String(classroom.money_goal_cents / 100) : "",
  });
  const suggestion = suggestMinuteGoal({ students: classroom.student_count, start: campaign.start_on, end: campaign.end_on });

  const submit = (e) => {
    e.preventDefault();
    patch(updateGoalsPath, { preserveScroll: true });
  };

  const minutesHelp = classroom.minute_goal
    ? `${formatNumber(classroom.total_minutes_logged)} of ${formatNumber(classroom.minute_goal)} minutes · ${percentOf(classroom.total_minutes_logged, classroom.minute_goal)}%`
    : null;
  const moneyHelp = classroom.money_goal_cents
    ? `${formatMoney(classroom.total_raised_cents)} of ${formatMoney(classroom.money_goal_cents)} · ${percentOf(classroom.total_raised_cents, classroom.money_goal_cents)}%`
    : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Classroom goals
        </CardTitle>
        <CardDescription>
          Optional targets for the class, shown as progress bars. They don't unlock anything on their own.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-5">
          <fieldset disabled={campaign.locked} className="grid gap-4 sm:grid-cols-2">
            <FormField
              name="minute_goal"
              label="Reading goal (minutes)"
              type="number"
              min="1"
              inputMode="numeric"
              placeholder={suggestion ? String(suggestion.goal) : "5000"}
              value={data.minute_goal}
              onChange={(e) => setData("minute_goal", e.target.value)}
              error={errors.minute_goal}
              note={minutesHelp}
            />
            <MoneyField
              name="money_goal_dollars"
              label="Fundraising goal"
              placeholder="1500"
              value={data.money_goal_dollars}
              onChange={(e) => setData("money_goal_dollars", e.target.value)}
              error={errors.money_goal_cents}
              note={moneyHelp}
            />
          </fieldset>

          {suggestion && !campaign.locked && String(suggestion.goal) !== String(data.minute_goal) && (
            <div className="flex flex-col gap-3 rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
              <p className="flex gap-2 text-muted-foreground">
                <Wand2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span>
                  Suggested: <strong className="text-foreground">{formatNumber(suggestion.goal)} minutes</strong> —{" "}
                  {classroom.student_count} students × {MINUTES_PER_DAY} min × {suggestion.days} days, plus a stretch.
                </span>
              </p>
              <Button type="button" size="sm" variant="outline" onClick={() => setData("minute_goal", String(suggestion.goal))}>
                Use it
              </Button>
            </div>
          )}

          {!campaign.locked && (
            <Button type="submit" disabled={processing}>{processing ? "Saving…" : "Save goals"}</Button>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
